import _ from 'lodash';
import moment from 'moment-timezone';
import { TFunction } from 'react-i18next';

import { AppointmentFilters, Props as FilterProps } from '../Filters';

import * as api from '../../../api';

export type SchedulingConfig = Pick<
  api.PrevisitFormField.PrevisitFormFieldWithConfig,
  | 'label'
  | 'helpDialog'
  | 'scheduleAppointmentSortBy'
  | 'scheduleAppointmentDisplayBy'
  | 'scheduleAppointmentStartTime'
  | 'scheduleAppointmentEndTime'
  | 'scheduleAppointmentCallSectionEnabled'
  | 'scheduleAppointmentCommentEnabled'
  | 'customConfigs'
  | 'fhirDynamicProviders'
> & {
  previsitFormFieldId: string;
};

export interface AvailableSlotsData {
  availableEhrTimeSlots: api.EhrTimeSlot.EhrTimeSlot[];
  practices: Record<string, api.EhrTimeSlot.PracticeTimeSlot>;
  providers: Record<string, api.User.UserIdentification>;
  fieldConfig: SchedulingConfig;
  patientOrder?: api.EhrTimeSlot.EhrTimeSlotPatientOrder;
  orders?: Record<string, api.PatientOrder.PatientOrder>;
  mainProviderUserId?: string;
}

export interface Props extends AvailableSlotsData {
  navigateToNextPage: () => Promise<void>;
  onAppointmentSelected: (appointment: api.EhrTimeSlot.EhrTimeSlot) => void;
  practice?: api.Practice.Practice;
  encounter?: api.Encounter.Encounter;
  patientPcpPreferred?: boolean;
  filterProps?: FilterProps;
}

export interface ViewProps
  extends Omit<Props, 'practice'>,
    Pick<Partial<Props>, 'practice'> {}

export interface PracticeSlotsGroup {
  practice: api.EhrTimeSlot.PracticeTimeSlot & { id: string };
  timeSlots: api.EhrTimeSlot.EhrTimeSlot[];
}

export type SelectedAppointment =
  | api.EhrTimeSlot.EhrTimeSlot
  | (api.EhrTimeSlot.EhrTimeSlot & api.EhrTimeSlot.TimeSlotConfirmation);

export interface SchedulingFormFieldValue {
  selectedAppointments: (api.EhrTimeSlot.EhrTimeSlot &
    api.EhrTimeSlot.TimeSlotConfirmation)[];
  seriesAppointmentCount?: number;
}

export type ProviderAndPractice = Omit<
  api.User.EhrProviderInformationPractice,
  'created' | 'updated' | 'id' | 'value'
>;
export type PracticeOnly = Omit<
  api.User.EhrProviderInformationPractice,
  | 'created'
  | 'updated'
  | 'id'
  | 'value'
  | 'firstName'
  | 'lastName'
  | 'prefix'
  | 'suffix'
  | 'previsitImageUrl'
  | 'phoneNumber'
  | 'specialties'
  | 'providerUserId'
>;

export const getFormattedDate = (
  date: string,
  timezone: string,
  t: TFunction,
) => {
  return (
    <>
      <strong>
        {moment(date)
          .tz(timezone)
          .format(
            moment(date).isSame(new Date(), 'year') ? 'MMM D' : 'MMM D YYYY',
          )}
      </strong>
      ,{' '}
      {moment(date)
        .tz(timezone)
        .calendar({
          sameDay: `[${t('Today')}]`,
          nextDay: `[${t('Tomorrow')}]`,
          nextWeek: 'dddd',
          lastDay: 'dddd',
          lastWeek: 'dddd',
          sameElse: 'dddd',
        })}
    </>
  );
};

export const showMaybeDayWord = (
  date: string,
  timezone: string,
  t: TFunction,
): string => {
  const momentDate = moment(date).tz(timezone);
  if (momentDate.isSame(moment().tz(timezone), 'd')) {
    return t('Today,');
  } else if (momentDate.isSame(moment().tz(timezone).add(1, 'day'), 'day')) {
    return t('Tmrw,');
  }
  return '';
};

export const applySortFilter = (
  practice1: PracticeSlotsGroup,
  practice2: PracticeSlotsGroup,
): number => {
  return practice1.timeSlots[0].startTime < practice2.timeSlots[0].startTime
    ? -1
    : 1;
};

export const applyTimeFilters = (
  slot: api.EhrTimeSlot.EhrTimeSlot,
  filters: AppointmentFilters,
  timezone: string,
): boolean => {
  const { startTime, endTime } = filters;
  if (!startTime || !endTime) return true;
  const slotStartTime = moment(slot.startTime).tz(timezone);
  if (
    startTime.getHours() > slotStartTime.hours() ||
    (startTime.getHours() === slotStartTime.hours() &&
      startTime.getMinutes() > slotStartTime.minutes())
  ) {
    return false;
  }
  if (
    endTime.getHours() < slotStartTime.hours() ||
    (endTime.getHours() === slotStartTime.hours() &&
      endTime.getMinutes() < slotStartTime.minutes())
  ) {
    return false;
  }
  return true;
};

export const loadProviderAvailableAppointments = async (args: {
  practiceId: string;
  formFieldId: string;
  providerId?: string;
  setOnCaptchaSuccess: (onCaptchaSuccess: (() => void) | null) => void;
}) => {
  const { practiceId, formFieldId, providerId } = args;
  try {
    const slots = await api.client.EhrTimeSlot.fetchAvailableTimeSlots({
      previsitFormFieldId: formFieldId,
      practiceIds: [practiceId],
      providerIds: providerId ? [providerId] : undefined,
    });
    return slots.availableEhrTimeSlots;
  } catch (err) {
    return [];
  }
};

export const getPracticeRecordFromConfigs = (
  ehrProviderInformation: api.User.EhrProviderInformationPractice[],
): Record<string, api.EhrTimeSlot.PracticeTimeSlot> => {
  const result: Record<string, api.EhrTimeSlot.PracticeTimeSlot> = {};
  ehrProviderInformation.forEach(
    (provider) =>
      (result[provider.practiceId] = _.pick(
        provider,
        'name',
        'address',
        'city',
        'state',
        'zip',
        'timezone',
        'latitude',
        'longitude',
        'practicePhoneNumber',
      )),
  );
  return result;
};

export const getProviderRecordFromConfigs = (
  ehrProviderInformation: api.User.EhrProviderInformationPractice[],
): Record<string, api.User.UserIdentification> => {
  const result: Record<string, api.User.UserIdentification> = {};
  ehrProviderInformation.forEach(
    (provider) => (result[provider.providerUserId] = provider),
  );
  return result;
};

export const isSelectedAppointmentBooked = (
  selectedAppointment?: SelectedAppointment,
): selectedAppointment is api.EhrTimeSlot.EhrTimeSlot &
  api.EhrTimeSlot.TimeSlotConfirmation => {
  if (!selectedAppointment) return false;
  if (
    'providerFirstName' in selectedAppointment ||
    'providerLastName' in selectedAppointment ||
    'practiceName' in selectedAppointment
  ) {
    return true;
  }
  return false;
};
